import { Link } from 'react-router-dom';
import { PublicLayout } from '../components/PublicLayout';
import { useWorkOSAuth } from '../lib/auth';
import { reportPostHogError } from '../lib/posthog';
import { appRoutes, publicRoutes, storeAuthReturnTo } from '../lib/routes';

const features = [
  {
    title: 'News und Kommunikation',
    text: 'Veröffentlichen Sie interne Meldungen, Bilder und Updates an genau die Mitglieder, die Zugriff haben.',
  },
  {
    title: 'Events und Anmeldungen',
    text: 'Termine, Teilnehmerlisten, Zusagen und Absagen bleiben an einem Ort und sind mobil verfügbar.',
  },
  {
    title: 'Mitgliederprofile',
    text: 'Mitglieder finden Kontakte, Rollen, Firmeninformationen und aktuelle Profile im geschützten Netzwerk.',
  },
  {
    title: 'Dokumente und Links',
    text: 'Wichtige Dateien, Social-Media-Kanäle und externe Ressourcen erscheinen direkt im Dashboard.',
  },
  {
    title: 'Verwaltung und Rollen',
    text: 'Verantwortliche Personen verwalten Mitglieder, Inhalte und Dokumente mit klar geregelten Zugriffsrechten.',
  },
  {
    title: 'Bexio-Integration',
    text: 'Backoffice-Teams können Kontakte importieren, Firmen verknüpfen und Synchronisationsläufe kontrollieren.',
  },
] as const;

const securityItems = [
  'Nur eingeladene Mitglieder erhalten Zugriff',
  'Jeder Verein sieht nur seine eigenen Inhalte',
  'Verwaltung bleibt bei den verantwortlichen Personen',
  'Persönlicher Support über hit-app@hit.swiss',
] as const;

export default function LandingPage() {
  const { user, signIn } = useWorkOSAuth();

  const handleSignIn = () => {
    storeAuthReturnTo(appRoutes.dashboard);
    void signIn({ state: { returnTo: appRoutes.dashboard } }).catch((error: unknown) => {
      console.error('Sign-in failed:', error);
      reportPostHogError(error, {
        area: 'auth',
        action: 'landing_sign_in',
        route: publicRoutes.landing,
      });
    });
  };

  return (
    <PublicLayout>
      <section className="landing-hero">
        <div className="landing-hero-content">
          <p className="landing-kicker">Für Vereine, Stiftungen und Organisationen</p>
          <h1>HiT-App</h1>
          <p className="landing-hero-copy">
            Die geschützte Plattform für Mitglieder, Events, News, Dokumente und Verwaltung in einem privaten Netzwerk.
          </p>
          <div className="landing-hero-actions">
            {user ? (
              <Link to={appRoutes.dashboard} className="btn btn-primary btn-lg">
                App öffnen
              </Link>
            ) : (
              <button type="button" onClick={handleSignIn} className="btn btn-primary btn-lg">
                Anmelden
              </button>
            )}
            <Link to="/demo" className="btn btn-secondary btn-lg">
              Demo anfragen
            </Link>
          </div>
        </div>

        <div className="landing-hero-media" aria-label="Einblicke in die HiT-App">
          <img
            src="/landing/home.jpeg"
            alt="HiT-App Dashboard auf einem Smartphone"
            className="landing-phone primary"
          />
          <img
            src="/landing/news-detail.jpeg"
            alt="News-Detailansicht der HiT-App"
            className="landing-phone secondary"
          />
          <img
            src="/landing/profile-redacted.jpeg"
            alt="Mitgliederprofil der HiT-App"
            className="landing-phone tertiary"
          />
        </div>
      </section>

      <section id="funktionen" className="public-section">
        <div className="public-section-header">
          <p className="landing-kicker">Funktionen</p>
          <h2>Alles Wichtige für Ihr Netzwerk</h2>
        </div>
        <div className="landing-feature-grid">
          {features.map((feature) => (
            <article key={feature.title} className="landing-feature">
              <h3>{feature.title}</h3>
              <p>{feature.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="sicherheit" className="public-section public-section-split">
        <div>
          <p className="landing-kicker">Sicherheit</p>
          <h2>Offen informieren, intern arbeiten</h2>
          <p>
            Die Website erklärt die HiT-App öffentlich. Mitgliederlisten, Vereinsinhalte und Verwaltungsbereiche bleiben
            im geschützten App-Bereich.
          </p>
        </div>
        <ul className="landing-security-list">
          {securityItems.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="landing-help-band">
        <div>
          <p className="landing-kicker">Support</p>
          <h2>Fragen zur Nutzung?</h2>
          <p>Der Hilfebereich erklärt Anmeldung, Dashboard, Profile, Events, Verwaltung und mobile Nutzung.</p>
        </div>
        <Link to={publicRoutes.help} className="btn btn-secondary">
          Hilfe öffnen
        </Link>
      </section>
    </PublicLayout>
  );
}
